import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  NumberFormatField,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { PeriodLimitFragment } from '@/fragments/__generated__/Limits';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  SetLossLimitMutation,
  SetLossLimitMutationVariables,
} from './__generated__/PlayerLossLimitsForm';

const query = graphql`
  query SanityPlayerLossLimitsForm {
    sanityPlayerLossLimitsForm {
      title {
        ...SanityLocaleString
      }
      dailyLabel {
        ...SanityLocaleString
      }
      weeklyLabel {
        ...SanityLocaleString
      }
      monthlyLabel {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const setLossLimitMutation = gql`
  mutation SetLossLimit(
    $playerId: ID!
    $amount: PositiveBigDecimal!
    $limitPeriodType: LimitTypeEnum!
  ) {
    setLossLimit(
      amount: $amount
      playerId: $playerId
      limitPeriodType: $limitPeriodType
    ) {
      id
      lossLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerLossLimitsForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
  limit: Nullable<PeriodLimitFragment>;
  currency: Nullable<string>;
}> = ({ playerId, limit, limitPeriodType, currency }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerLossLimitsFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerLossLimitsForm;

  const [setLossLimitForPlayerState, setLossLimitForPlayer] = useMutation<
    SetLossLimitMutation,
    SetLossLimitMutationVariables
  >(setLossLimitMutation);

  const defaultValues = {
    amount: limit?.value,
  };

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (values: typeof defaultValues) => {
    if (playerId) {
      setLossLimitForPlayer({
        playerId,
        amount: values.amount ?? 0,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
          return;
        }
        if (close) {
          close();
        }
      });
    }
  };

  if (!form || !defaultValues) {
    return null;
  }
  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <NumberFormatField
            title={t(
              limitPeriodType === LimitTypeEnum.Day
                ? form.dailyLabel
                : limitPeriodType === LimitTypeEnum.Week
                  ? form.weeklyLabel
                  : form.monthlyLabel,
            )}
            suffix={` ${currency}`}
            name="amount"
            id="PlayerLossLimitsForm__amount"
            className="col-span-full"
          />

          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={setLossLimitForPlayerState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerLossLimitsForm;
